// app/javascript/controllers/dashboard/demo_profile_builder_controller.js
import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'userType',
    'presetSelector',
    'locale',
    'product',
    'programLevel',
    'programLevelWrapper',
    'submittedProgramLevel',
    'destination',
    'destinationWrapper',
    'jobRole',
    'jobTrack',
    'coachingProgram',
    'coachingHours',
    'onDemand',
    'submit',
    'version',
    'versionWrapper',
    'candidateProfileWrapper',
    'coachingFieldsWrapper',
  ]

  static values = {
    supportedLocales: Array,
    availableLocales: Array,
    defaultProducts: Array,
    defaultProgramLevels: Array,
    jobRoles: Array,
    jobTracks: Array,
    coachingPrograms: Array,
    presets: Object,
  }

  connect() {
    this.resetSubmitState()
    this.userTypeChanged()
  }

  userTypeChanged() {
    if (this.isClientAdminUserType()) {
      this.prepareClientAdminMode()
    } else {
      this.prepareCandidateMode()
    }

    this.syncSubmittedProgramLevel()
  }

  localeChanged() {
    if (this.isClientAdminUserType()) return

    this.resetSelect(this.productTarget, 'Product')
    this.resetProgramLevel()
    this.hideCareerDevelopmentFields()
    this.disableSubmittedProgramLevel()

    const selectedLocale = this.localeTarget.value
    const products = this.supportedLocalesValue
      .filter((entry) => entry.locale === selectedLocale)
      .map((entry) => entry.product)

    if (products.length === 0) {
      this.disableSelect(this.productTarget)
      return
    }

    this.enableSelect(this.productTarget, 'Product')
    this.defaultProductsValue.forEach(([label, value]) => {
      if (products.includes(value))
        this.appendOption(this.productTarget, label, value)
    })
  }

  productChanged() {
    if (this.isClientAdminUserType()) return

    const selectedProduct = this.productTarget.value

    this.resetProgramLevel()
    this.hideCareerDevelopmentFields()
    this.hideVersionField()

    const matchingProgramLevels = this.supportedLocalesValue
      .filter(
        (entry) =>
          entry.locale === this.localeTarget.value &&
          entry.product === selectedProduct,
      )
      .map((entry) => entry.program_level)

    if (matchingProgramLevels.length === 0) {
      this.disableSelect(this.programLevelTarget)
      this.disableSubmittedProgramLevel()
      this.resetSubmitState()
      return
    }

    this.enableSelect(
      this.programLevelTarget,
      selectedProduct === 'CAREER_DEVELOPMENT' ? 'Coaching' : 'Program Level',
    )

    if (selectedProduct === 'OUTPLACEMENT') {
      this.showVersionField()
    } else {
      this.hideVersionField()
    }

    if (selectedProduct === 'CAREER_DEVELOPMENT') {
      this.appendOption(this.programLevelTarget, 'No Coaching', 'BRONZE')
      this.appendOption(
        this.programLevelTarget,
        'With Coaching',
        'with_coaching_bronze',
      )
      this.showCandidateProfileFields()
      this.enableSubmittedProgramLevel()
    } else {
      this.defaultProgramLevelsValue.forEach(([label, value]) => {
        if (!matchingProgramLevels.includes(value)) return
        this.appendOption(
          this.programLevelTarget,
          this.programLevelLabel(selectedProduct, label),
          value,
        )
      })
      this.disableSubmittedProgramLevel()
    }

    this.syncSubmittedProgramLevel()
    this.resetSubmitState()
  }

  programLevelChanged() {
    if (this.isClientAdminUserType()) return

    const value = this.programLevelTarget.value

    if (!value) {
      this.hideCareerDevelopmentFields()
      this.syncSubmittedProgramLevel()
      this.resetSubmitState()
      return
    }

    if (this.isCareerDevelopmentProduct()) {
      this.showCandidateProfileFields()
      this.populateCandidateProfileFields()

      if (value === 'with_coaching_bronze') {
        const preserved = {
          coachingProgram: this.hasCoachingProgramTarget
            ? this.coachingProgramTarget.value
            : '',
          coachingHours: this.hasCoachingHoursTarget
            ? this.coachingHoursTarget.value
            : '',
          onDemand: this.hasOnDemandTarget ? this.onDemandTarget.value : '',
        }

        this.showCoachingFields()
        this.populateCoachingFields(preserved)
      } else {
        this.hideCoachingFields()
      }
    }

    this.syncSubmittedProgramLevel()

    if (this.hasSubmitTarget) this.submitTarget.disabled = false
  }

  onDemandChanged() {
    if (this.isClientAdminUserType()) return

    this.syncSubmittedProgramLevel()
  }

  destinationChanged() {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = !this.destinationTarget.value
    }
  }

  resetProgramLevel() {
    this.disableSelect(this.programLevelTarget)
    this.enableSelect(this.programLevelTarget, 'Program Level')
    this.disableSelect(this.programLevelTarget)
  }

  resetSubmitState() {
    if (this.hasSubmitTarget) this.submitTarget.disabled = true
  }

  showCandidateProfileFields() {
    this.candidateProfileWrapperTargets.forEach((element) => {
      element.classList.remove('hidden')
    })
  }

  hideCareerDevelopmentFields() {
    this.hideCandidateProfileFields()
    this.hideCoachingFields()
  }

  hideCandidateProfileFields() {
    this.candidateProfileWrapperTargets.forEach((element) => {
      element.classList.add('hidden')
    })

    if (this.hasJobRoleTarget) this.disableSelect(this.jobRoleTarget)
    if (this.hasJobTrackTarget) this.disableSelect(this.jobTrackTarget)
  }

  showCoachingFields() {
    this.coachingFieldsWrapperTargets.forEach((element) => {
      element.classList.remove('hidden')
    })
  }

  showVersionField() {
    if (this.hasVersionWrapperTarget) {
      this.versionWrapperTarget.classList.remove('hidden')
    }
    if (this.hasVersionTarget) {
      this.versionTarget.disabled = false
      this.versionTarget.value = 1
    }
  }

  hideVersionField() {
    if (this.hasVersionWrapperTarget) {
      this.versionWrapperTarget.classList.add('hidden')
    }
    if (this.hasVersionTarget) {
      this.versionTarget.disabled = true
      this.versionTarget.value = 0
    }
  }

  hideCoachingFields() {
    this.coachingFieldsWrapperTargets.forEach((element) => {
      element.classList.add('hidden')
    })

    if (this.hasCoachingProgramTarget)
      this.disableSelect(this.coachingProgramTarget)
    if (this.hasCoachingHoursTarget)
      this.disableSelect(this.coachingHoursTarget)
    if (this.hasOnDemandTarget) this.disableSelect(this.onDemandTarget)
  }

  populateCandidateProfileFields() {
    if (this.hasJobRoleTarget && this.jobRoleTarget.options.length <= 1) {
      this.enableSelect(this.jobRoleTarget, 'Job Role')
      this.jobRolesValue.forEach(([label, value]) =>
        this.appendOption(this.jobRoleTarget, label, value),
      )
    } else if (this.hasJobRoleTarget) {
      this.jobRoleTarget.disabled = false
    }

    if (this.hasJobTrackTarget && this.jobTrackTarget.options.length <= 1) {
      this.enableSelect(this.jobTrackTarget, 'Job Track')
      this.jobTracksValue.forEach(([label, value]) =>
        this.appendOption(this.jobTrackTarget, label, value),
      )
    } else if (this.hasJobTrackTarget) {
      this.jobTrackTarget.disabled = false
    }
  }

  populateCoachingFields(preserved = {}) {
    const preservedProgram =
      preserved.coachingProgram ??
      (this.hasCoachingProgramTarget ? this.coachingProgramTarget.value : '')

    const preservedHours =
      preserved.coachingHours ??
      (this.hasCoachingHoursTarget ? this.coachingHoursTarget.value : '')

    const preservedOnDemand =
      preserved.onDemand ??
      (this.hasOnDemandTarget ? this.onDemandTarget.value : '')

    if (this.hasCoachingProgramTarget) {
      this.enableSelect(this.coachingProgramTarget, 'Coaching Program')
      this.coachingProgramsValue.forEach((value) =>
        this.appendOption(this.coachingProgramTarget, value, value),
      )
      if (preservedProgram) this.coachingProgramTarget.value = preservedProgram
    }

    if (this.hasCoachingHoursTarget) {
      this.enableSelect(this.coachingHoursTarget, 'Coaching Hours')
      ;['On', 'Off'].forEach((value) =>
        this.appendOption(this.coachingHoursTarget, value, value),
      )
      if (preservedHours) this.coachingHoursTarget.value = preservedHours
    }

    if (this.hasOnDemandTarget) {
      this.enableSelect(this.onDemandTarget, 'On Demand')
      ;['Off', 'On'].forEach((value) =>
        this.appendOption(this.onDemandTarget, value, value),
      )
      if (preservedOnDemand) this.onDemandTarget.value = preservedOnDemand
    }
  }

  syncSubmittedProgramLevel() {
    if (!this.hasProgramLevelTarget) return

    if (this.isClientAdminUserType()) {
      this.disableSubmittedProgramLevel()
      this.programLevelTarget.removeAttribute('name')
      return
    }

    if (this.isCareerDevelopmentProduct()) {
      this.enableSubmittedProgramLevel()
      this.submittedProgramLevelTarget.value =
        this.resolvedCareerDevelopmentProgramLevel()
      this.programLevelTarget.removeAttribute('name')
    } else {
      this.disableSubmittedProgramLevel()
      this.programLevelTarget.name = 'user[program_level]'
    }
  }

  resolvedCareerDevelopmentProgramLevel() {
    const selectedProgramLevel = this.programLevelTarget.value

    if (!selectedProgramLevel) return ''

    if (selectedProgramLevel === 'BRONZE') {
      return 'BRONZE'
    }

    if (selectedProgramLevel === 'with_coaching_bronze') {
      if (this.hasOnDemandTarget && this.onDemandTarget.value === 'On') {
        return 'SOLO_PLUS'
      }

      return 'BRONZE'
    }

    return selectedProgramLevel
  }

  enableSubmittedProgramLevel() {
    if (!this.hasSubmittedProgramLevelTarget) return
    this.submittedProgramLevelTarget.disabled = false
  }

  disableSubmittedProgramLevel() {
    if (!this.hasSubmittedProgramLevelTarget) return
    this.submittedProgramLevelTarget.disabled = true
    this.submittedProgramLevelTarget.value = ''
  }

  isCareerDevelopmentProduct() {
    return (
      this.hasProductTarget && this.productTarget.value === 'CAREER_DEVELOPMENT'
    )
  }

  enableSelect(element, placeholder) {
    element.innerHTML = ''
    const option = document.createElement('option')
    option.value = ''
    option.textContent = this.placeholderText(placeholder)
    element.appendChild(option)
    element.disabled = false
  }

  disableSelect(element) {
    element.innerHTML = ''
    const option = document.createElement('option')
    option.textContent = 'N/A'
    element.appendChild(option)
    element.disabled = true
  }

  resetSelect(element, placeholder) {
    this.enableSelect(element, placeholder)
    element.value = ''
  }

  appendOption(element, label, value) {
    const option = document.createElement('option')
    option.value = value
    option.textContent = label
    element.appendChild(option)
  }

  placeholderText(label) {
    return `--- Choose ${label} ---`
  }

  programLevelLabel(product, label) {
    if (product === 'OUTPLACEMENT' || product === 'CAREER_DEVELOPMENT')
      return label
    if (label === 'Solo') return 'No Coaching'
    if (label === 'Solo Plus') return 'With Coaching'
    return label
  }

  presetSelected() {
    if (!this.hasPresetSelectorTarget) return

    const presetKey = this.presetSelectorTarget.value
    if (!presetKey) return

    const preset = this.presetsValue[presetKey]
    if (!preset || !preset.user) return

    this.applyPreset(preset.user)
  }

  applyPreset(user) {
    if (user.type && this.hasUserTypeTarget) {
      this.userTypeTarget.value = user.type
      this.userTypeChanged()
    }

    if (user.locale && this.hasLocaleTarget) {
      this.localeTarget.value = user.locale
      this.localeChanged()
    }

    if (user.product && this.hasProductTarget) {
      this.productTarget.value = user.product
      this.productChanged()
    }

    if (user.program_level && this.hasProgramLevelTarget) {
      this.programLevelTarget.value =
        user.program_level === 'SOLO_PLUS'
          ? 'with_coaching_bronze'
          : user.program_level
      this.programLevelChanged()
    }

    if (user.destination && this.hasDestinationTarget) {
      this.destinationTarget.value = user.destination
    }

    if (user.candidate_profile) {
      this.populateCandidateProfileFields()

      if (user.candidate_profile.job_role && this.hasJobRoleTarget) {
        this.jobRoleTarget.value = user.candidate_profile.job_role
      }

      if (user.candidate_profile.job_track && this.hasJobTrackTarget) {
        this.jobTrackTarget.value = user.candidate_profile.job_track
      }
    }

    if (user.other) {
      this.showCoachingFields()
      this.populateCoachingFields()

      if (user.other.coaching_program && this.hasCoachingProgramTarget) {
        this.coachingProgramTarget.value = user.other.coaching_program
      }

      if (user.other.coaching_hours && this.hasCoachingHoursTarget) {
        this.coachingHoursTarget.value = user.other.coaching_hours
      }

      if (this.hasOnDemandTarget) {
        if (user.program_level === 'SOLO_PLUS') {
          this.onDemandTarget.value = 'On'
        } else if (user.other.on_demand) {
          this.onDemandTarget.value = user.other.on_demand
        }
      }

      this.onDemandChanged()
    }

    if (this.hasSubmitTarget) this.submitTarget.disabled = false
    this.syncSubmittedProgramLevel()
  }

  prepareClientAdminMode() {
    this.hideCareerDevelopmentFields()

    if (this.hasLocaleTarget) this.disableSelect(this.localeTarget)
    if (this.hasProductTarget) this.disableSelect(this.productTarget)
    if (this.hasProgramLevelTarget) this.disableSelect(this.programLevelTarget)
    if (this.hasDestinationTarget) this.disableSelect(this.destinationTarget)
    this.hideVersionField()

    this.disableSubmittedProgramLevel()

    if (this.hasSubmitTarget) this.submitTarget.disabled = false
  }

  prepareCandidateMode() {
    if (this.hasLocaleTarget) {
      this.enableSelect(this.localeTarget, 'Locale')
      this.supportedLocaleOptions().forEach(([label, value]) => {
        this.appendOption(this.localeTarget, label, value)
      })
    }

    this.resetSelect(this.productTarget, 'Product')
    this.resetProgramLevel()
    this.hideCareerDevelopmentFields()
    this.hideVersionField()
    this.disableSubmittedProgramLevel()
    this.resetSubmitState()

    if (this.hasDestinationTarget) {
      this.enableSelect(this.destinationTarget, 'Destination')
      this.appendOption(this.destinationTarget, 'Onboarding', 'onboarding')
      this.appendOption(this.destinationTarget, 'Platform', 'platform')
      this.destinationTarget.value = ''
    }

    if (this.localeTarget.value) {
      this.localeChanged()
    }
  }

  isClientAdminUserType() {
    return this.hasUserTypeTarget && this.userTypeTarget.value === 'ClientAdmin'
  }

  supportedLocaleOptions() {
    return this.hasAvailableLocalesValue ? this.availableLocalesValue : []
  }
}
